import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { usePropertyContactQuery } from '@/hooks/queries/usePropertyDashboard';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Users } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ContactTabProps {
  propertyId?: string;
}

export function ContactTab({ propertyId }: ContactTabProps) {
  const { t, i18n } = useTranslation();
  const isRTL = useMemo(() => i18n.language === 'ar', [i18n.language]);

  // Use React Query hook
  const { data: contactData, isLoading } = usePropertyContactQuery(
    propertyId || ''
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle
          className={cn(
            'flex items-center gap-2',
            isRTL ? 'flex-row-reverse' : 'flex-row'
          )}
        >
          <Users className="w-5 h-5" />
          {t('properties.details.contactTab.title')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-muted-foreground">{t('loading')}</div>
          </div>
        ) : contactData ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="space-y-2">
              <div
                className={cn(
                  'text-sm font-medium text-muted-foreground',
                  isRTL ? 'text-right' : 'text-left'
                )}
              >
                {t('properties.details.contactTab.contactPerson')}
              </div>
              <div
                className={cn(
                  'text-base font-medium',
                  isRTL ? 'text-right' : 'text-left'
                )}
              >
                {contactData.contact_person ||
                  t('properties.details.contactTab.notSpecified')}
              </div>
            </div>

            <div className="space-y-2">
              <div
                className={cn(
                  'text-sm font-medium text-muted-foreground',
                  isRTL ? 'text-right' : 'text-left'
                )}
              >
                {t('properties.details.contactTab.contactEmail')}
              </div>
              <div
                className={cn(
                  'text-base font-medium',
                  isRTL ? 'text-right' : 'text-left'
                )}
              >
                {contactData.contact_email ? (
                  <a
                    href={`mailto:${contactData.contact_email}`}
                    className="text-blue-600 hover:text-blue-800 hover:underline"
                  >
                    {contactData.contact_email}
                  </a>
                ) : (
                  t('properties.details.contactTab.notSpecified')
                )}
              </div>
            </div>

            <div className="space-y-2">
              <div
                className={cn(
                  'text-sm font-medium text-muted-foreground',
                  isRTL ? 'text-right' : 'text-left'
                )}
              >
                {t('properties.details.contactTab.contactPhone')}
              </div>
              <div
                className={cn(
                  'text-base font-medium',
                  isRTL ? 'text-right' : 'text-left'
                )}
              >
                {contactData.contact_phone ? (
                  <a
                    href={`tel:${contactData.contact_phone}`}
                    className="text-blue-600 hover:text-blue-800 hover:underline"
                  >
                    {contactData.contact_phone}
                  </a>
                ) : (
                  t('properties.details.contactTab.notSpecified')
                )}
              </div>
            </div>

            {contactData.location && (
              <div className="space-y-2 md:col-span-3">
                <div
                  className={cn(
                    'text-sm font-medium text-muted-foreground',
                    isRTL ? 'text-right' : 'text-left'
                  )}
                >
                  {t('properties.details.contactTab.location')}
                </div>
                <div
                  className={cn(
                    'text-base font-medium',
                    isRTL ? 'text-right' : 'text-left'
                  )}
                >
                  {contactData.location}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-12 text-muted-foreground">
            {t('properties.details.contactTab.noData')}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
